import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import message from '../components/message';
import reducers from './reducers';
import { updateLocation, history } from './location';

export function resolveBlob(blob, fileName) {
  const aLink = document.createElement('a');
  aLink.href = URL.createObjectURL(blob);
  aLink.setAttribute('download', fileName);
  document.body.appendChild(aLink);
  aLink.click();
  document.body.removeChild(aLink);
}

function callAPIMiddleware({ dispatch, getState }) {
  return next => action => {
    const { types, callAPI, shouldCallAPI, payload = {}, callback } = action;
    if (!types) {
      // Normal action: pass it on
      return next(action);
    }

    if (
      !Array.isArray(types) ||
      types.length !== 3 ||
      !types.every(type => typeof type === 'string')
    ) {
      throw new Error('Expected an array of three string types.');
    }

    if (typeof callAPI !== 'function') {
      throw new Error('Expected callAPI to be a function.');
    }

    if (typeof shouldCallAPI === 'function' && !shouldCallAPI(getState())) {
      const ret = {};
      ret.then = () => undefined; // fix the no then function error
      return ret;
    }

    const [requestType, successType, failureType] = types;

    dispatch({ ...payload, type: requestType });

    return callAPI(getState()).then(
      response => {
        if (response instanceof Blob) {
          const newPayload = {
            ...payload,
            data: response,
            type: successType,
            msg: response.message,
            success: true,
          };
          dispatch(newPayload);
          callback && callback(newPayload, dispatch, getState());
          return newPayload;
        }
        if (
          `${response.code}` == '200' ||
          `${response.code}` == '10000' ||
          `${response.code}` == '1'
        ) {
          const newPayload = {
            ...payload,
            data: response.data,
            type: successType,
            msg: response.message,
            success: true,
          };
          dispatch(newPayload);
          callback && callback(newPayload, dispatch, getState());
          return newPayload;
        }
        // 客户管理诊所权限提示
        if (response && `${response.code}` == '60000') {
          const newPayload = {
            ...payload,
            data: response.message,
            isNoClinicAuthority: true, // 没有诊所权限
            type: successType,
            msg: response.message,
            success: true,
          };
          dispatch(newPayload);
          callback && callback(newPayload, dispatch, getState());
          return newPayload;
        }
        const failurePayload = {
          code: response.code,
          msg: response.message,
          type: failureType,
          success: false,
        };
        !payload.disabledMsg && message.error(failurePayload.msg || '系统错误');
        dispatch(failurePayload);
        callback && callback(failurePayload, dispatch, getState());
        return failurePayload;
      },
      error => {
        const errorPayload = {
          msg: error,
          type: failureType,
          success: false,
        };
        message.error(error);
        dispatch(errorPayload);
        callback && callback(errorPayload, dispatch, getState());
      }
    );
  };
}

export default (initialState = {}) => {
  // ======================================================
  // Middleware Configuration
  // ======================================================
  const middleware = [thunk, callAPIMiddleware];

  // ======================================================
  // Store Enhancers
  // ======================================================
  const enhancers = [];

  let composeEnhancers = compose;

  if (__LOCAL__) {
    const composeWithDevToolsExtension =
      window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
    if (typeof composeWithDevToolsExtension === 'function') {
      composeEnhancers = composeWithDevToolsExtension;
    }
  }

  // ======================================================
  // Store Instantiation and HMR Setup
  // ======================================================
  const store = createStore(
    reducers(),
    initialState,
    composeEnhancers(applyMiddleware(...middleware), ...enhancers)
  );
  store.asyncReducers = {};

  // To unsubscribe, invoke `store.unsubscribeHistory()` anytime
  store.unsubscribeHistory = history.listen(updateLocation(store));
  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(reducers(store.asyncReducers));
    });
  }

  return store;
};
